// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as TagsActions from "features/tags/actions"
import { CreateFavouriteTagRequest, FavouriteTag } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { grpc } from "app/rpc/executor"
import { tagServiceClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type CreateFavouriteTagRpc = (request: CreateFavouriteTagRequest) => Observable<FavouriteTag>

export const createFavouriteTag = (createFavouriteTagRpc: CreateFavouriteTagRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createFavouriteTag.match),
      mergeMap(action =>
        of(action.payload)
          .pipe(
            mergeMap(createFavouriteTagRpc),
            map(t => t.toObject()),
            map(Slice.createFavouriteTagFulfilled),
            catchError(handleError),
          )
      ),
    )

const createFavouriteTagRpc = (request: CreateFavouriteTagRequest): Observable<FavouriteTag> =>
  grpc(() => tagServiceClient.createFavouriteTag(request, null))

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_favourite_tag_not_found", message: "Tag not found." }),
          messageType: "ERROR",
        })),
        // fetch recent tags to refresh list
        of(TagsActions.fetchRecentTags())
      )
    }
  }

  return of(
    showSnackBar({
      message: t({ id: "msg_favourite_tag_failed", message: "Failed to favourite tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    })
  )
}

export default createFavouriteTag(createFavouriteTagRpc)
